import { isRetryableError } from './paymentErrorHandler'

const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const calculateDelay = (attempt, config) => {
  const delay =
    config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt)
  return Math.min(delay, config.maxDelayMs)
}

/**
 * Retry wrapper with exponential backoff for async operations.
 */
export const withRetry = async (
  operation,
  config = DEFAULT_RETRY_CONFIG,
  context = {}
) => {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config }
  let lastError = null

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(
          `[Retry] Attempt ${attempt + 1}/${finalConfig.maxRetries + 1}`,
          context
        )
      }

      const result = await operation()

      if (attempt > 0) {
        console.log(`[Retry] Success after ${attempt + 1} attempts`, context)
      }

      return result
    } catch (error) {
      lastError = error

      const shouldRetry = isRetryableError(error)
      const hasRetriesLeft = attempt < finalConfig.maxRetries

      if (!shouldRetry) {
        console.error(
          '[Retry] Non-retryable error, failing immediately',
          context,
          error
        )
        throw error
      }

      if (!hasRetriesLeft) {
        console.error('[Retry] Max retries reached, failing', context, error)
        throw error
      }

      const delay = calculateDelay(attempt, finalConfig)
      console.warn(
        `[Retry] Attempt ${attempt + 1} failed, retrying in ${delay}ms`,
        context,
        error
      )

      await sleep(delay)
    }
  }

  throw lastError
}
